import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { CommercialDocumentVersion } from "@bw/shared/types/generated";


export function downloadExactAgreement(document: CommercialDocumentVersion) {
  const blob = new Blob([document.content], { type: `${document.media_type};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  const extension = document.media_type === "text/html" ? "html" : "txt";
  anchor.download = `${document.template_key}-${document.template_version}.${extension}`
    .replace(/[^a-zA-Z0-9._-]/g, "-");
  anchor.click();
  URL.revokeObjectURL(url);
}


export function ExactAgreementDocument({
  document,
  unavailableCopy = "An exact agreement document has not been published for this version.",
}: {
  document: CommercialDocumentVersion | null | undefined;
  unavailableCopy?: string;
}) {
  if (!document) return <Alert severity="warning">{unavailableCopy}</Alert>;
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden" }}>
      <Stack spacing={1.5} sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
          sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}>
          <Box>
            <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 800 }}>
              Exact agreement copy
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {document.template_key} · {document.template_version} · {document.language}
            </Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={() => downloadExactAgreement(document)}>
            Download copy
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Chip size="small" label={document.media_type} />
          <Chip size="small" label={`Approval reference: ${document.approval_reference}`} />
        </Stack>
        <Box sx={{ borderRadius: 2, bgcolor: "action.hover", p: 2, maxHeight: 360, overflow: "auto" }}>
          <Typography component="pre" variant="body2" sx={{ fontFamily: "inherit", m: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {document.content}
          </Typography>
        </Box>
        <Typography color="text.secondary" variant="caption" sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
          SHA-256 {document.content_sha256}
        </Typography>
      </Stack>
    </Paper>
  );
}
